import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, Search, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getLocalizedPath } from "@/utils/routing";
import { useSSRStatus } from "@/contexts/SSRContext";

const NotFound = () => {
  const { t, i18n } = useTranslation();
  const ssrStatus = useSSRStatus();

  // Signal 404 to SSR so the server returns HTTP 404 (not 200) — fixes soft-404 in GSC
  if (ssrStatus && typeof window === 'undefined') {
    ssrStatus.notFound = true;
  }
  
  // Get localized paths based on current language
  const currentLang = i18n.language as 'sk' | 'cs' | 'da' | 'en' | 'de';
  const modulesPath = getLocalizedPath('/moduly', currentLang);
  const pricingPath = getLocalizedPath('/cennik', currentLang);
  const contactPath = getLocalizedPath('/kontakt', currentLang);

  return (
    <div className="min-h-screen bg-background">
      {/* Title and description in the site's language, also in the server-rendered HTML. */}
      <Helmet>
        <title>{t('notFound.seo.title')}</title>
        <meta name="description" content={t('notFound.seo.description')} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Header />
      
      <main className="flex items-center justify-center min-h-screen pt-16">
        <div className="container-custom text-center py-20">
          <div className="max-w-2xl mx-auto space-y-8">
            {/* 404 Error */}
            <div className="space-y-4">
              <div className="text-8xl md:text-9xl font-bold text-primary/20 select-none">
                404
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                {t('notFound.title')}
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                {t('notFound.description')}
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center">
              <Button asChild size="lg" className="w-full sm:w-auto sm:min-w-[200px]">
                <Link to="/">
                  <Home className="h-5 w-5 mr-2" />
                  {t('notFound.buttons.home')}
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto sm:min-w-[200px]">
                <Link to={modulesPath}>
                  <Search className="h-5 w-5 mr-2" />
                  {t('notFound.buttons.features')}
                </Link>
              </Button>
            </div>

            {/* Quick Links */}
            <div className="pt-8 border-t border-border/50">
              <h2 className="text-lg font-semibold mb-4">{t('notFound.quickLinks.title')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <Link 
                  to={modulesPath} 
                  className="p-4 rounded-lg border border-border hover:bg-accent transition-colors"
                >
                  <FileText className="h-6 w-6 mx-auto mb-2 text-primary" />
                  <div className="font-medium">{t('notFound.quickLinks.allFeatures')}</div>
                  <div className="text-sm text-muted-foreground">{t('notFound.quickLinks.featuresDesc')}</div>
                </Link>
                <Link 
                  to={pricingPath} 
                  className="p-4 rounded-lg border border-border hover:bg-accent transition-colors"
                >
                  <div className="text-2xl mb-2">💰</div>
                  <div className="font-medium">{t('notFound.quickLinks.pricing')}</div>
                  <div className="text-sm text-muted-foreground">{t('notFound.quickLinks.pricingDesc')}</div>
                </Link>
                <Link 
                  to={contactPath} 
                  className="p-4 rounded-lg border border-border hover:bg-accent transition-colors"
                >
                  <div className="text-2xl mb-2">📞</div>
                  <div className="font-medium">{t('notFound.quickLinks.contact')}</div>
                  <div className="text-sm text-muted-foreground">{t('notFound.quickLinks.contactDesc')}</div>
                </Link>
              </div>
            </div>

            {/* Go Back Button */}
            <div className="pt-4">
              <Button 
                variant="ghost" 
                onClick={() => window.history.back()}
                className="text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('notFound.buttons.back')}
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFound;
